import {Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ApiService} from 'src/app/shared/services/api.service';
import {AccountType, EMAIL_PATTERN, OtpType} from '../../shared/models/user';
import {DevicesService, FormService, LangService, LoaderService, NavigateService, PinComponent} from '@satoris/ngx-satoris';
import {SecureStorageService} from 'src/app/shared/services/secure-storage.service';
import {getStoredItem, setStoredItem} from 'src/app/shared/utils/storage';
import {SyncService} from 'src/app/shared/services/sync.service';
import {environment} from '../../../environments/environment';
import {parseError} from 'src/app/shared/utils/string';
import {TimeoutError} from 'rxjs';
import {Router} from '@angular/router';
import {SmsAutofillService} from 'src/app/shared/services/sms-autofill.service';

// What was typed before a trip to the language screen, in memory only: a password must not reach navigation state or storage.
let draft: {signType: OtpType, askOtp: boolean, values: Record<string, string>} | undefined;

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SignInComponent implements OnInit, OnDestroy {
  @ViewChild(PinComponent) pinComponent: PinComponent;
  form: FormGroup;
  submitted: boolean;
  accounts: AccountType[] = getStoredItem('jwts') || [];
  askOtp: boolean;
  signType: OtpType = OtpType.EMAIL;
  showOTPCorrect: boolean;
  showOTPError: boolean;
  backupButton = false;
  otpDisabled: boolean;
  connecting: boolean;
  otpDisabledTime = 10000;
  otp: string;
  OtpType = OtpType;
  debounceTimer: number = 0;
  appHash: string;
  data: { signType?: OtpType, email?: string, phone?: string, restoreDraft?: boolean };

  constructor(public forms: FormService,
              public nav: NavigateService,
              public api: ApiService,
              private formBuilder: FormBuilder,
              private lang: LangService,
              private loader: LoaderService,
              private secure: SecureStorageService,
              private device : DevicesService,
              private sync: SyncService,
              private router: Router,
              private smsAutofill: SmsAutofillService) {
    this.data = <any> this.router.getCurrentNavigation().extras.state;
    this.changeOtpType(this.data?.signType || OtpType.EMAIL);
    this.api.signOut(false);
    this.api.mobileBack(() => this.nav.to('home'));
  }

  ngOnInit(): void {
    this.api.signOut(true, false);
    this.setForm();
    if(this.data?.restoreDraft && draft) this.restore(draft);
    draft = undefined;

    if(this.device.isDevices('cordova-android') && this.smsAutofill.isAvailable()) {
      this.smsAutofill.getAppHash().then(hash => {
        this.appHash = hash;
        console.debug('[SignIn] App hash for backend:', hash);
      }).catch(err => {
        //TODO: Probably we can show here a warning, that autofill feature is not available
        console.error('[SignIn] Failed to get app hash:', err);
      });
    }
  }

  setForm() {
    this.form = this.formBuilder.group({
      email: [this.data?.email || '', [
        Validators.required,
        Validators.pattern(EMAIL_PATTERN)
      ]],
      ['phone' + environment.country]: [this.data?.phone || ''],
      password: [this.form?.get('password')?.value ? this.form.get('password').value : '', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(30)
      ]],
      otp: ['', Validators.pattern(/[0-9]{6}/)]
    }, {
      /*validator: this.forms.customPatternValidator('password', [/[A-Z]/, /[a-z]/, /[0-9]/, /[~`!@#$%^&*()\-_+={[}\]|:;"'<,>.?/]/], 3)*/
    });
  }

  submitForm() {
    if(!this.sync.isOnline) {
      this.loader.loading(true, {type: 'warn', message: this.lang.transform('no.connection.signIn')});
    } else {
      this.submitted = true;
      if(this.forms.getFormErrors(this.form).length < 1) {
        this.connecting = true;
        this.submitted = false;
        this.api.userPlaceId = undefined;
        this.loginWeak();
      }
    }
  }

  loginWeak() {
    this.showOTPError = false;
    this.otpDisabled = false;
    this.showOTPCorrect = false;
    this.api.signOut(true, false);
    this.loader.loading(true);
    return this.api.consumeLoginWeak(this.form.get(this.signType === OtpType.EMAIL ? 'email' : ('phone' + environment.country)).value, this.form.get('password').value, this.otp,
      undefined, undefined, undefined, false, false, undefined, this.signType === OtpType.PHONE ? this.appHash : undefined).then((status: number) => {
      this.loader.loading(false);
      if(status === 204) {
        this.debounceTimer = this.signType === OtpType.PHONE ? 180000 : 5000;
        this.loader.loading(false);
        this.connecting = false;
        this.askOtp = true;
        if(this.device.isDevices('cordova-android') && this.signType === OtpType.PHONE && this.smsAutofill.isAvailable()) this.startSmsAutoFill();
        return;
      } else if(this.askOtp) {
        this.showOTPCorrect = true;
      }

      const index = this.accounts.findIndex(object => object.email === this.form.get('email').value);
      if(index > -1) {
        this.accounts[index] = {
          name: this.form.get(this.signType === OtpType.EMAIL ? 'email' : ('phone' + environment.country)).value,
          email: this.form.get(this.signType === OtpType.EMAIL ? 'email' : ('phone' + environment.country)).value,
          jwt: this.api.accountJwt?.jwt
        };
      } else {
        this.accounts.push({
          name: this.form.get(this.signType === OtpType.EMAIL ? 'email' : ('phone' + environment.country)).value,
          email: this.form.get(this.signType === OtpType.EMAIL ? 'email' : ('phone' + environment.country)).value,
          jwt: this.api.accountJwt?.jwt
        });
      }
      setStoredItem('jwts', this.accounts);

      if(this.device.isDevices('cordova')) {
        this.loader.loading(true);
        return this.api.exchange(undefined, undefined, true, true).then((res) => this.secure.setEncryptSecureDatas('longJWt', res.jwt).then(() => this.finishLogin())).catch(() => {
          this.api.signOut(true, false);
          this.loader.loading(false);
          this.submitted = false;
          this.otpDisabled = false;
          this.connecting = false;
          return false;
        });
      } else {
        return this.finishLogin();
      }
    }, err => {
      this.loader.loading(false);
      const error = parseError(err);
      if(!this.sync.isOnline) {
        return this.loader.loading(true, {type: 'warn', message: this.lang.transform('no.connection.signIn')});
      }
      if(error.baseError === 'user.notConfirmed'){
        return this.nav.to('account-valid', undefined, {state: {signType: this.signType, accountToActivate: this.form.get(this.signType === OtpType.EMAIL ? 'email' : 'phone').value}});
      }
      if(this.askOtp){
        this.backupButton = true;
        this.showOTPError = true;
        this.resetOtpField();
        this.otpDisabled = true;
        setTimeout(() => {
          this.otpDisabled = false;
          this.connecting = false;
        }, this.otpDisabledTime);
      }
      if(!(err instanceof TimeoutError) && err.status !== 0) {
        if((typeof err === 'string' ) && err.includes('Could not validate user')) {
          this.loader.loading(true, {type: 'warn', message: this.lang.transform('otp.login.fail')});
        } else if(error.baseError.includes('user.otpTooSoon')) {
          const timer = Number(error.baseError.split(' ')[1]) * 1000;
          this.debounceTimer = timer;
          this.loader.loading(true, {type: 'warn', message: 'user.otpTooSoon.DYN.server'});
        } else {
          this.loader.loading(true, {type: 'warn', message: error.tradError});
        }
      }
      this.submitted = false;
    });
  }

  checkOTPCode(e: string[]) {
    const codeOk = e ? e?.length >= 6 : this.otp?.length >= 6;
    this.otp = e ? e.join('') : this.otp;
    if(codeOk) {
      if(this.form.controls.otp.valid) {
        this.connecting = true;
        return this.loginWeak();
      } else {
        this.resetOtpField();
        this.loader.loading(true, {type: 'warn', message: 'otp.pattern'});
      }
    }
  }

  private finishLogin() {
    return this.api.info(undefined, true).then(() => {
      this.loader.loading(false);
      if(this.api.userRole?.isCustomer || this.api.userRole?.isSuperAdmin) {
        this.pinRedirect();
      } else {
        this.api.signOut(true);
        this.loader.loading(true, {type: 'warn', message: this.lang.transform('sign_in.login_as_customer')});
      }
    }).catch(() => {
      this.api.signOut(true, false);
      this.loader.loading(false);
      this.backupButton = true;
      this.submitted = false;
      this.otpDisabled = false;
      this.connecting = false;
    });
  }

  private pinRedirect() {
    this.loader.loading(false);
    if(this.api.userInfo?.pinHash) {
      if(this.device.isDevices('cordova')) {
        // skip PIN only if it's persisted locally, else let the guard route to /secret-code-valid to store it
        this.secure.getEncryptSecureDatas('pin').then(pin => {
          if(pin) {
            this.api.pinUnlocked = true;
          }
          this.nav.to('user');
        });
      } else {
        this.api.pinUnlocked = true;
        this.nav.to('user');
      }
    } else {
      this.nav.to('secret-code');
    }
  }

  closeOtp() {
    this.askOtp = false;
    this.resetOtpField();
    this.form.get('otp').setValue('');
    this.showOTPError = false;
    // this.form.get(this.signType === OtpType.EMAIL ? 'email' : 'phone').setValue('');
    this.form.get('password').setValue('');
  }

  changeOtpType(to?: OtpType) {
    if(to !== undefined) {
      this.signType = to;
    } else {
      this.signType = this.signType === OtpType.EMAIL ? OtpType.PHONE : OtpType.EMAIL;
    }
    setTimeout(() => {
      if(this.signType === OtpType.EMAIL) {
        this.form.get('phone' + environment.country).clearValidators();
        this.form.get('phone' + environment.country).updateValueAndValidity();
        this.form.get('email').setValidators([Validators.required, Validators.pattern(EMAIL_PATTERN)]);
        this.form.get('email').updateValueAndValidity();
      } else {
        this.form.get('phone' + environment.country).setValidators([Validators.required, Validators.pattern(environment.phonePattern)]);
        this.form.get('phone' + environment.country).updateValueAndValidity();
        this.form.get('email').clearValidators();
        this.form.get('email').updateValueAndValidity();
      }
    });
  }

  private startSmsAutoFill() {
    this.smsAutofill.startWatching().then(otp => {
      if(otp && otp.length === 6) {
        this.form.patchValue({otp: otp});
        this.otp = otp;
        this.checkOTPCode(otp.split(''));
      } else {
        console.debug('[SignIn] No valid OTP received, user will enter manually');
      }
    }).catch(err => {
      console.error('[SignIn] SMS auto-fill failed:', err);
    });
  }

  private resetOtpField() {
    if(this.pinComponent?.formGrp && this.pinComponent?.formCtrlName) {
      this.pinComponent.formGrp.get(this.pinComponent.formCtrlName)?.setValue('');
      this.pinComponent.formGrp.get(this.pinComponent.formCtrlName)?.markAsPristine();
      this.pinComponent.formGrp.get(this.pinComponent.formCtrlName)?.markAsUntouched();
    }
    this.pinComponent.codeArray = [];
    this.otp = '';
  }

  ngOnDestroy(): void {
    if(this.smsAutofill.isCurrentlyListening()) this.smsAutofill.stopWatching();
    if(this.router.getCurrentNavigation()?.finalUrl?.toString().startsWith('/choose-lang')) {
      const values = this.form.getRawValue();
      delete values.otp;
      draft = {signType: this.signType, askOtp: this.askOtp, values};
    }
  }

  // The code already sent stays valid, so a return to the code step asks for it again instead of sending another one.
  private restore(saved: typeof draft) {
    this.form.patchValue(saved.values);
    this.changeOtpType(saved.signType);
    if(!saved.askOtp) return;
    this.debounceTimer = this.signType === OtpType.PHONE ? 180000 : 5000;
    this.askOtp = true;
    if(this.device.isDevices('cordova-android') && this.signType === OtpType.PHONE && this.smsAutofill.isAvailable()) this.startSmsAutoFill();
  }
}
